import React, {useEffect, useState, useCallback, useRef} from 'react'
import {approveJobPost, rejectJobPost, closeJobPost, getJobPosts} from '../../../api/HiringAPI'
import {Link} from 'react-router-dom'
import {Message} from '../../../_metronic/helpers'
import ListPagination from '../../components/ListPagination'
import StatusBadge from '../../components/StatusBadge/StatusBadge'

type JobPostTab = 'APPROVED' | 'ON_HOLD' | 'ALL' | 'CLOSED'
const jobStatusColors = { OPEN: 'success', CLOSED: 'secondary', ON_HOLD: 'warning' }

const JobPostsList: React.FC = () => {
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState('')
  const [totalRecords, setTotalRecords] = useState(0)
  const [activeTab, setActiveTab] = useState<JobPostTab>('APPROVED')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const requestIdRef = useRef(0)

  const fetchJobs = useCallback(async () => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError('')
    setRows([])
    setTotalRecords(0)
    const params: any = { limit: pageSize, offset: (page - 1) * pageSize }
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim()
    if (activeTab === 'APPROVED') {
      params.isPublished = true
      params.status = 'OPEN'
    }
    if (activeTab === 'ON_HOLD') params.status = 'ON_HOLD'
    if (activeTab === 'CLOSED') params.status = 'CLOSED'
    try {
      const res: any = await getJobPosts(params)
      if (res?.success === false || (res?.statusCode && res.statusCode >= 400)) {
        throw new Error(res?.message || 'Failed to load job posts')
      }
      const payload = res?.data && !Array.isArray(res.data) ? res.data : res
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload?.result) ? payload.result : []
      const total = Number(payload?.total ?? payload?.pagination?.total ?? payload?.queryData?.total ?? list.length)
      if (requestId === requestIdRef.current) {
        setRows(list)
        setTotalRecords(Number.isFinite(total) ? total : list.length)
      }
    } catch (fetchError: any) {
      if (requestId === requestIdRef.current) {
        setRows([])
        setTotalRecords(0)
        setError(fetchError?.message || 'Failed to load job posts')
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [activeTab, page, pageSize, debouncedSearch])

  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [search])

  const changeTab = (tab: JobPostTab) => {
    setActiveTab(tab)
    setPage(1)
  }

  const clearSearch = () => {
    setSearch('')
    setDebouncedSearch('')
    setPage(1)
    searchInputRef.current?.focus()
  }

  const handlePublicJD = (id: string) => {
    if (!id || id === 'undefined' || id === 'null') {
      Message('Error: Missing job ID', 'error')
      return
    }
    window.open(`/jd/${id}`, '_blank')
  }

  const copyApplyLink = (id: string) => {
    if (!id) {
      Message('Error: Missing job ID', 'error')
      return
    }
    
    try {
      const url = `${window.location.origin}/application-form?jobPostId=${id}`
      
      // Use a fallback method to copy to clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          Message('Link copied to clipboard', 'success')
        }).catch(() => {
          // Fallback: use the deprecated method
          copyToClipboardFallback(url)
        })
      } else {
        // Use fallback for older browsers or secure contexts
        copyToClipboardFallback(url)
      }
    } catch (error) {
      console.error('Failed to copy link:', error)
      Message('Failed to copy link to clipboard', 'error')
    }
  }

  const copyToClipboardFallback = (text: string) => {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    try {
      document.execCommand('copy')
      Message('Link copied to clipboard', 'success')
    } catch (error) {
      console.error('Fallback copy failed:', error)
      Message('Failed to copy link to clipboard', 'error')
    } finally {
      document.body.removeChild(textarea)
    }
  }

  const onApprove = async (id: string) => {
    await approveJobPost(id)
    await fetchJobs()
  }

  const onReject = async (id: string) => {
    await rejectJobPost(id)
    await fetchJobs()
  }

  const onClose = async (id: string) => {
    await closeJobPost(id)
    await fetchJobs()
  }

  return (
    <div className='card'>
      <div className='card-header d-flex align-items-center'>
        <h3 className='card-title mb-0 fw-bold'>Hiring — Job Posts</h3>
        <div className='ms-auto'>
          <Link to='/hiring/jobs/new' className='btn btn-primary fw-bold px-6 py-3 d-inline-flex align-items-center justify-content-center'>Create JD</Link>
        </div>
      </div>
      <div className='card-body p-5'>
        <div className='d-flex align-items-center gap-3 mb-5'>
          <button type='button' className={`btn ${activeTab==='APPROVED'?'btn-primary':'btn-light'}`} onClick={()=>changeTab('APPROVED')}>Approved</button>
          <button type='button' className={`btn ${activeTab==='ON_HOLD'?'btn-primary':'btn-light'}`} onClick={()=>changeTab('ON_HOLD')}>On Hold</button>
          <button type='button' className={`btn ${activeTab==='ALL'?'btn-primary':'btn-light'}`} onClick={()=>changeTab('ALL')}>All</button>
          <button type='button' className={`btn ${activeTab==='CLOSED'?'btn-primary':'btn-light'}`} onClick={()=>changeTab('CLOSED')}>Closed</button>
        </div>

        <div className='mb-4'>
          <div className='input-group w-100 w-md-350px'>
            <input
              ref={searchInputRef}
              type='search'
              className='form-control'
              placeholder='Search by job title, department, or location'
              aria-label='Search by job title, department, or location'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search.length > 0 && <button type='button' className='btn btn-light' aria-label='Clear search' title='Clear search' onClick={clearSearch}>×</button>}
          </div>
        </div>

        {error && <div className='alert alert-danger' role='alert'>{error}</div>}

        <div className='table-responsive' style={{overflowX: 'visible', overflowY: 'visible'}}>
          <table className='table align-middle table-row-dashed table-striped mb-0'>
            <thead>
              <tr className='text-muted fw-semibold text-uppercase'>
                <th className='min-w-200px'>Title</th>
                <th className='min-w-150px'>Department</th>
                <th className='min-w-125px'>Location</th>
                <th className='min-w-125px'>Employment</th>
                <th className='min-w-125px'>Status</th>
                <th className='min-w-125px text-end'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className='text-center py-10'>Loading...</td></tr>
              ) : error ? (
                <tr><td colSpan={6} className='text-center py-10'>Job posts could not be loaded.</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className='text-center py-10'>No job posts found</td></tr>
              ) : (
                rows.map((j: any) => (
                  <tr key={j.id}>
                    <td className='fw-bold'>{j.title}</td>
                    <td>{j.department}</td>
                    <td>{j.location || '-'}</td>
                    <td>{j.employmentType || '-'}</td>
                    <td><StatusBadge status={j.status} colorMap={jobStatusColors} /></td>
                    <td className='text-end'>
                      <div className='dropdown'>
                        <button className='btn btn-sm btn-light-primary dropdown-toggle' data-bs-toggle='dropdown'>Actions</button>
                        <div className='dropdown-menu dropdown-menu-end'>
                          {(() => {
                            const isApproved = Boolean(j.isPublished) && j.status === 'OPEN'
                            const isPending = !j.isPublished && j.status === 'OPEN'
                            const isOnHold = j.status === 'ON_HOLD'

                            if (isApproved) {
                              // Approved → only public actions and optional close
                              return (
                                <>
                                  <Link className='dropdown-item' to={`/hiring/applications`}>View applications</Link>
                                  <button className='dropdown-item' onClick={() => handlePublicJD(j.id)}>Publish JD</button>
                                  <button className='dropdown-item' onClick={() => copyApplyLink(j.id)}>Copy apply link</button>
                                  <a className='dropdown-item' href={`/application-form?jobPostId=${j.id}`} target='_blank' rel='noreferrer'>Open form</a>
                                  <div className='dropdown-divider'></div>
                                  <button className='dropdown-item text-danger' onClick={() => onClose(j.id)}>Close JD</button>
                                </>
                              )
                            }

                            if (isPending || isOnHold) {
                              // Pending/On Hold → only approval choices
                              return (
                                <>
                                  <button className='dropdown-item text-success' onClick={() => onApprove(j.id)}>Approve & publish</button>
                                  <button className='dropdown-item text-warning' onClick={() => onReject(j.id)}>Reject</button>
                                </>
                              )
                            }

                            // Fallback (e.g., CLOSED): keep minimal actions
                            return (
                              <>
                                <button className='dropdown-item text-success' onClick={() => onApprove(j.id)}>Approve & publish</button>
                              </>
                            )
                          })()}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!error && !loading && <ListPagination
          page={page}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
          disabled={loading}
        />}
      </div>
    </div>
  )
}

export default JobPostsList


